import { useState, useCallback, useRef } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { fetchLogs } from "../../operations/logsService";
import { LogsResponse, LogEntry, LogFilters, LogSummary } from "./types";

dayjs.extend(utc);

// Recompute the level tallies from the logs we currently hold, so the summary
// cards stay in sync with what's loaded as the user scrolls / appends batches.
// Non-count fields (byService, bySupplier, byEnvironment) are carried over from
// the backend's first-batch summary.
function summarize(logs: LogEntry[], base?: LogSummary): LogSummary {
  const byLevel = { error: 0, warn: 0, info: 0, other: 0 };
  for (const l of logs) {
    const lvl = (l.level || "").toUpperCase();
    if (lvl === "ERROR") byLevel.error++;
    else if (lvl === "WARN" || lvl === "WARNING") byLevel.warn++;
    else if (lvl === "INFO") byLevel.info++;
    else byLevel.other++;
  }
  return {
    byService: {},
    bySupplier: null,
    byEnvironment: {},
    ...(base || {}),
    totalLogs: logs.length,
    byLevel,
  };
}

export default function useLogs() {
  const [data, setData] = useState<LogsResponse | null>(null);
  const [loading, setLoading] = useState(false); // initial / fresh fetch
  const [loadingMore, setLoadingMore] = useState(false); // append (scroll) fetch
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // The filters from the last fresh load — reused (with a moved `to` bound) by loadMore.
  const filtersRef = useRef<LogFilters | null>(null);
  // Accumulated logs across the initial fetch + every appended batch.
  const logsRef = useRef<LogEntry[]>([]);
  // Backend summary from the fresh load — kept for its non-count fields while we
  // recompute the level tallies over the accumulated logs.
  const baseSummaryRef = useRef<LogSummary | undefined>(undefined);

  const runFetch = useCallback(async (filters: LogFilters, append: boolean) => {
    // Cancel any in-flight request so rapid re-fetches don't race.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const result: LogsResponse = await fetchLogs(filters, controller.signal);
      if (!result?.success) {
        // Surface order per backend guide: detail -> error -> errors.
        const msg =
          result?.detail ||
          result?.error ||
          (result?.errors && result.errors.join(", ")) ||
          "Request failed";
        throw new Error(msg);
      }
      const batch = result.logs || [];
      const limit = filters.limit || 0;
      if (append) {
        // Cursor pagination overlaps the boundary minute, so drop any log we
        // already hold (matched by id) before appending the rest.
        const seen = new Set(
          logsRef.current.filter((l) => l.id).map((l) => l.id)
        );
        const fresh = batch.filter((l) => !l.id || !seen.has(l.id));
        logsRef.current = logsRef.current.concat(fresh);
        const merged = logsRef.current;
        const summary = summarize(merged, baseSummaryRef.current);
        setData((prev) =>
          prev
            ? { ...prev, logs: merged, summary }
            : { ...result, logs: merged, summary }
        );
        // A short batch means we've drained the window; an all-duplicate batch
        // means the cursor can't advance — stop in both cases.
        setHasMore(batch.length >= limit && fresh.length > 0);
      } else {
        logsRef.current = batch;
        baseSummaryRef.current = result.summary;
        setData({ ...result, logs: batch, summary: summarize(batch, result.summary) });
        setHasMore(batch.length >= limit);
      }
    } catch (err: any) {
      // Swallow cancellations from the abort above.
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      const errData = err?.response?.data;
      setError(
        errData?.detail ||
          errData?.error ||
          (errData?.errors && errData.errors.join(", ")) ||
          err?.message ||
          "Failed to fetch logs"
      );
      if (!append) {
        setData(null);
        logsRef.current = [];
      }
      setHasMore(false);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, []);

  // Fresh load: replaces the current result set and remembers the filters.
  const load = useCallback(
    (filters: LogFilters) => {
      filtersRef.current = filters;
      logsRef.current = [];
      setHasMore(false);
      runFetch(filters, false);
    },
    [runFetch]
  );

  // Append the next continuous batch: same window start, but end the window at
  // the oldest log we currently hold (timestamp cursor). Newest-first ordering
  // from the backend means "older" logs are what remain to be fetched.
  const loadMore = useCallback(() => {
    if (!filtersRef.current || loading || loadingMore || !hasMore) return;
    const logs = logsRef.current;
    if (!logs.length) return;
    let oldest = "";
    for (const l of logs) {
      if (l.timestamp && (!oldest || l.timestamp < oldest)) oldest = l.timestamp;
    }
    if (!oldest) {
      setHasMore(false);
      return;
    }
    const d = dayjs.utc(oldest);
    const nextFilters: LogFilters = {
      ...filtersRef.current,
      toDate: d.format("YYYY-MM-DD"),
      toTime: d.format("HH:mm"),
    };
    runFetch(nextFilters, true);
  }, [hasMore, loading, loadingMore, runFetch]);

  return { data, loading, loadingMore, hasMore, error, load, loadMore };
}
